import { cloneDeep } from 'lodash';
import { formatISO, parseISO } from 'date-fns';
import Decimal from 'big.js';
import Domain from './domain';
import InvoiceDto from './invoice-dto';
import JobCode from '../job/job-code';
import Service from './service';
import Techspec from '../job/techspec';

export default class Invoice
{
    public dto!: InvoiceDto;

    public constructor(dto: InvoiceDto)
    {
        this.dto = Object.assign({}, dto);

        this._techspecId = this.dto.techspec ? this.dto.techspec.id : null;
        this._serviceId = this.dto.service ? this.dto.service.id : null;
        this._domainId = this.dto.domain ? this.dto.domain.id : null;
        this._jobCodeId = this.dto.jobCode ? this.dto.jobCode.id : null;
    }

    public get id(): number|null
    {
        return this.dto.id;
    }

    public set id(id: number|null)
    {
        this.dto.id = id;
    }

    public get techspec(): Techspec|null
    {
        if (!this.dto.techspec)
        {
            return null;
        }

        return new Techspec(this.dto.techspec);
    }

    private _techspecId: number|null = null;

    public get techspecId(): number|null
    {
        return this._techspecId;
    }

    public set techspecId(id: number|null)
    {
        this._techspecId = id;
    }

    public get service(): Service|null
    {
        if (!this.dto.service)
        {
            return null;
        }

        return new Service(this.dto.service);
    }

    private _serviceId: number|null = null;

    public get serviceId(): number|null
    {
        return this._serviceId;
    }

    public set serviceId(id: number|null)
    {
        this._serviceId = id;
    }

    public get domain(): Domain|null
    {
        if (!this.dto.domain)
        {
            return null;
        }

        return new Domain(this.dto.domain);
    }

    private _domainId: number|null = null;

    public get domainId(): number|null
    {
        return this._domainId;
    }

    public set domainId(id: number|null)
    {
        this._domainId = id;
    }

    public get invoiceNumber(): string
    {
        return this.dto.invoiceNumber;
    }

    public set invoiceNumber(invoiceNumber: string)
    {
        this.dto.invoiceNumber = invoiceNumber;
    }

    public get jobCode(): JobCode|null
    {
        if (!this.dto.jobCode)
        {
            return null;
        }

        return new JobCode(this.dto.jobCode);
    }

    private _jobCodeId: number|null = null;

    public get jobCodeId(): number|null
    {
        return this._jobCodeId;
    }

    public set jobCodeId(id: number|null)
    {
        this._jobCodeId = id;
    }

    public get title(): string
    {
        return this.dto.title;
    }

    public set title(title: string)
    {
        this.dto.title = title;
    }

    public get notes(): string|null
    {
        return this.dto.notes;
    }

    public set notes(notes: string|null)
    {
        this.dto.notes = notes;
    }

    public get paymentTerms(): string|null
    {
        return this.dto.paymentTerms;
    }

    public set paymentTerms(paymentTerms: string|null)
    {
        this.dto.paymentTerms = paymentTerms;
    }

    public get amount(): Decimal
    {
        return new Decimal(this.dto.amount);
    }

    public set amount(amount: Decimal)
    {
        this.dto.amount = amount.toString();
    }

    public get adwordsAmount(): Decimal
    {
        return new Decimal(this.dto.adwordsAmount);
    }

    public set adwordsAmount(adwordsAmount: Decimal)
    {
        this.dto.adwordsAmount = adwordsAmount.toString();
    }

    public get miscAmount(): Decimal
    {
        return new Decimal(this.dto.miscAmount);
    }

    public set miscAmount(miscAmount: Decimal)
    {
        this.dto.miscAmount = miscAmount.toString();
    }

    public get dateRequested(): Date
    {
        return parseISO(this.dto.dateRequested);
    }

    public set dateRequested(dateRequested: Date)
    {
        this.dto.dateRequested = formatISO(dateRequested);
    }

    public get dateSent(): Date|null
    {
        if (!this.dto.dateSent)
        {
            return null;
        }

        return parseISO(this.dto.dateSent);
    }

    public set dateSent(dateSent: Date|null)
    {
        if (!dateSent)
        {
            this.dto.dateSent = null;
            return;
        }

        this.dto.dateSent = formatISO(dateSent);
    }

    public get datePaid(): Date|null
    {
        if (!this.dto.datePaid)
        {
            return null;
        }

        return parseISO(this.dto.datePaid);
    }

    public set datePaid(datePaid: Date|null)
    {
        if (!datePaid)
        {
            this.dto.datePaid = null;
            return;
        }

        this.dto.datePaid = formatISO(datePaid);
    }

    public get dateReminder1(): Date|null
    {
        if (!this.dto.dateReminder1)
        {
            return null;
        }

        return parseISO(this.dto.dateReminder1);
    }

    public set dateReminder1(dateReminder1: Date|null)
    {
        if (!dateReminder1)
        {
            this.dto.dateReminder1 = null;
            return;
        }

        this.dto.dateReminder1 = formatISO(dateReminder1);
    }

    public get dateReminder2(): Date|null
    {
        if (!this.dto.dateReminder2)
        {
            return null;
        }

        return parseISO(this.dto.dateReminder2);
    }

    public set dateReminder2(dateReminder2: Date|null)
    {
        if (!dateReminder2)
        {
            this.dto.dateReminder2 = null;
            return;
        }

        this.dto.dateReminder2 = formatISO(dateReminder2);
    }

    public get dateCreditNote(): Date|null
    {
        if (!this.dto.dateCreditNote)
        {
            return null;
        }

        return parseISO(this.dto.dateCreditNote);
    }

    public set dateCreditNote(dateCreditNote: Date|null)
    {
        if (!dateCreditNote)
        {
            this.dto.dateCreditNote = null;
            return;
        }

        this.dto.dateCreditNote = formatISO(dateCreditNote);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public get params(): {[key: string]: any}
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dto: any = cloneDeep(this.dto);

        dto.techspec = this.techspecId ? `/techspecs/${ this.techspecId }` : null;
        dto.service = this.serviceId ? `/services/${ this.serviceId }` : null;
        dto.domain = this.domainId ? `/domains/${ this.domainId }` : null;
        dto.jobCode = this.jobCodeId ? `/job_codes/${ this.jobCodeId }` : null;

        return dto;
    }
}